import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../AuthContext";
import { trackEvent } from "../utils/analytics";

function ArrowRight() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12h14"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13 5l7 7-7 7"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function QA({ q, a }) {
  return (
    <details className="group rounded-2xl border border-blue-100 bg-white shadow-sm">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5">
        <div className="text-base font-extrabold text-blue-950 md:text-lg">{q}</div>
        <span className="text-blue-950/70 transition group-open:rotate-180">
          <ChevronDown />
        </span>
      </summary>
      <div className="px-6 pb-6 leading-relaxed text-gray-700">{a}</div>
    </details>
  );
}

function SectionEyebrow({ children, dark = false }) {
  return (
    <div
      className={
        dark
          ? "inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[13px] font-extrabold uppercase tracking-[0.13em] text-white/85"
          : "inline-flex rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-[13px] font-extrabold uppercase tracking-[0.13em] text-blue-950"
      }
    >
      {children}
    </div>
  );
}

export default function WelcomeScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const PUBLIC = process.env.PUBLIC_URL || "";
  const [isScrolled, setIsScrolled] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    trackEvent("home_view");
  }, []);

  const isSignedIn = !!user;
  const primaryLabel = useMemo(() => {
    if (isSignedIn) return "Go to Dashboard";
    return "Start Free";
  }, [isSignedIn]);

  const headerCtaClass = useMemo(() => {
    const base =
      "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm transition sm:px-5";
    if (isScrolled) {
      return `${base} bg-blue-950 text-white font-extrabold shadow hover:shadow-md`;
    }
    return `${base} bg-white/90 text-blue-950 font-extrabold shadow hover:bg-white hover:shadow-md`;
  }, [isScrolled]);

  const HERO_IMAGE = `${PUBLIC}/images/hero/welcome-hero.jpg`;
  const HERO_FALLBACK = `${PUBLIC}/images/hero/momestate132.jpg`;
  const [heroSrc, setHeroSrc] = useState(HERO_IMAGE);

  const PHOTO_EFFICIENCY_IMAGE = `${PUBLIC}/images/hero/inventory-dashboard-hero.png`;
  const PHOTO_EFFICIENCY_FALLBACK = `${PUBLIC}/images/hero/herosalesnapshot.png?v=2`;
  const PHOTO_SOURCE_IMAGE = `${PUBLIC}/images/hero/example-dashboard-hero.jpg`;
  const PHOTO_SOURCE_FALLBACK = `${PUBLIC}/images/hero/momestate132.jpg`;
  const CHECKOUT_MODAL_IMAGE = `${PUBLIC}/images/hero/checkout-modal-hero.png`;
  const START_SMALL_IMAGE = `${PUBLIC}/images/hero/startsmall.jpg`;
  const START_SMALL_FALLBACK = `${PUBLIC}/images/hero/momestate495.jpg`;
  const trackNav = (to, placement) => {
    const suffix = placement ? `__${placement}` : "";
    trackEvent(`nav_home_to_${to}${suffix}`);
  };

  const goDashboard = (placement) => {
    trackNav("estate-sales-pipeline", placement);
    navigate("/estate-sales-pipeline");
  };

  const goRegister = (placement) => {
    trackEvent(`home_start_free_click__${placement}`);
    trackNav("register", placement);
    navigate("/register");
  };

  const goPrimary = (placement) => {
    if (isSignedIn) return goDashboard(placement);
    return goRegister(placement);
  };

  const goTo = (destination, placement) => {
    const target = destination.replace("/", "") || "home";
    trackNav(target, placement);
    navigate(destination);
  };

  const scrollToSection = (id, placement) => {
    trackEvent(`nav_home_to_${id}__${placement}`);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const workflowSteps = [
    {
      title: "Create inventory faster",
      desc: "Upload the staged sale photos you already use for marketing. One photo can create multiple editable items, with no inventory-item limit.",
    },
    {
      title: "Price and print faster",
      desc: "Review suggested prices, make changes, and batch-print the tags you need.",
    },
    {
      title: "Reduce typing at checkout",
      desc: "Use live search or QR codes to add items to carts, automatically record what sold, and quickly add items that were not inventoried in advance.",
    },
  ];

  const featureGroups = [
    {
      title: "Prepare faster",
      items: [
        "Create multiple items from one photo",
        "Price from phone or computer",
        "Batch-print selected tags",
        "Keep rooms and sections organized",
      ],
    },
    {
      title: "Control the sale",
      items: [
        "Keep prices searchable for staff",
        "Recover prices when tags disappear",
        "See asking totals before opening",
        "Review value by room or category",
      ],
    },
    {
      title: "Move checkout faster",
      items: [
        "Search or scan items",
        "Build carts on logged-in devices",
        "Add untagged items at checkout",
        "Cash, outside card, QR, or reader",
      ],
    },
    {
      title: "Account for the sale",
      items: [
        "See every item sold",
        "Track payment, discounts, and tax",
        "Review category totals",
        "Export the sale record",
      ],
    },
  ];

  const primaryCtaClass =
    "inline-flex min-h-[58px] items-center justify-center gap-2 rounded-2xl bg-white px-8 text-base font-extrabold text-blue-950 shadow-[0_12px_30px_rgba(0,0,0,0.22)] transition hover:shadow-[0_16px_36px_rgba(0,0,0,0.26)]";

  const darkPrimaryCtaClass =
    "inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl bg-blue-950 px-8 text-base font-extrabold text-white shadow transition hover:bg-blue-900";

  return (
    <div className="flex w-full flex-col bg-white">
      <header className="fixed inset-x-0 top-0 z-50">
        <div
          className={
            isScrolled
              ? "absolute inset-0 border-b border-gray-200 bg-white/90 backdrop-blur"
              : "absolute inset-0 bg-blue-950/55 backdrop-blur"
          }
        />
        <div className="relative mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <span
              className={
                isScrolled
                  ? "text-lg font-extrabold tracking-wider text-blue-950"
                  : "text-lg font-extrabold tracking-wider text-white"
              }
              aria-label="Scucci Systems"
            >
              Scucci Systems
            </span>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
            <div className="relative hidden sm:block">
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setFeaturesOpen((open) => !open);
                  trackEvent("home_features_menu_toggle");
                }}
                aria-expanded={featuresOpen}
                className={
                  isScrolled
                    ? "inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-blue-950 transition hover:bg-gray-50"
                    : "inline-flex items-center gap-2 rounded-xl border border-white/20 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
                }
              >
                Features
                <span className={`transition ${featuresOpen ? "rotate-180" : ""}`}>
                  <ChevronDown />
                </span>
              </motion.button>

              {featuresOpen && (
                <div className="absolute right-0 top-[calc(100%+12px)] z-50 w-[calc(100vw-2rem)] max-w-[680px] rounded-[28px] border border-slate-200 bg-white p-6 text-left shadow-[0_24px_70px_rgba(15,23,42,0.22)]">
                  <div className="grid grid-cols-2 gap-x-8 gap-y-7">
                    {featureGroups.map((group) => (
                      <div key={group.title}>
                        <div className="text-[15px] font-extrabold uppercase tracking-[0.1em] text-blue-950">
                          {group.title}
                  </div>
                        <div className="mt-3 space-y-2">
                          {group.items.map((item) => (
                            <div key={item} className="flex items-center gap-3 text-[15px] font-semibold text-slate-700">
                              <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-950" />
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 flex items-center justify-between gap-5 border-t border-slate-200 pt-5">
                    <div className="text-[15px] font-semibold text-slate-600">
                      Create the inventory once. Use it from pricing through the final sale record.
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFeaturesOpen(false);
                        scrollToSection("workflow", "features_menu");
                      }}
                      className="inline-flex items-center gap-2 text-[15px] font-extrabold text-blue-950"
                    >
                      See how it works
                      <ArrowRight />
                    </button>
                  </div>
                </div>
              )}
            </div>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => goTo("/pricing", "header")}
              className={
                isScrolled
                  ? "rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold text-blue-950 transition hover:bg-gray-50 sm:px-4"
                  : "rounded-xl border border-white/20 px-3 py-2 text-sm font-semibold text-white transition hover:bg-white/10 sm:px-4"
              }
            >
              Pricing
            </motion.button>
            {!isSignedIn && (
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate("/signin")}
                className={
                  isScrolled
                    ? "rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-blue-950 transition hover:bg-gray-50 sm:px-5"
                    : "rounded-xl border border-white/25 px-4 py-2 text-sm font-bold text-white transition hover:bg-white/10 sm:px-5"
                }
              >
                Sign In
              </motion.button>
            )}
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => goPrimary("header")}
              className={headerCtaClass}
            >
              {primaryLabel}
              {isSignedIn ? <ArrowRight /> : null}
            </motion.button>
          </div>
        </div>
      </header>

      <section className="relative w-full overflow-hidden border-b border-blue-900/40">
        <img
          src={heroSrc}
          onError={() => {
            if (heroSrc !== HERO_FALLBACK) setHeroSrc(HERO_FALLBACK);
          }}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center saturate-[0.65] brightness-[0.8]"
          draggable="false"
        />
        <div className="absolute inset-0 bg-blue-950/58" />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-950 via-blue-950/92 to-blue-950/58" />
        <div className="absolute inset-y-0 left-0 w-[64%] bg-[linear-gradient(90deg,rgba(7,19,67,0.84)_0%,rgba(7,19,67,0.48)_58%,rgba(7,19,67,0)_100%)]" />
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-blue-950/30 to-transparent" />

        <div className="relative z-10 mx-auto max-w-[92rem] px-4 sm:px-6">
          <div className="grid min-h-[760px] items-center gap-12 py-28 lg:grid-cols-[minmax(0,0.88fr)_minmax(620px,1.12fr)] xl:grid-cols-[minmax(0,0.86fr)_minmax(720px,1.14fr)]">
            <div className="max-w-[740px] text-white">
              <div className="mb-5 text-sm font-bold uppercase tracking-[0.16em] text-white/70">
                Built for estate-sale teams
              </div>

              <motion.h1
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-[680px] text-[42px] font-extrabold leading-[0.98] tracking-[-0.035em] sm:text-[56px] lg:text-[64px]"
              >
                Create inventory. Print tags. Keep checkout moving.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="mt-7 max-w-[650px] text-[18px] leading-[1.65] text-white/86 sm:text-[21px]"
              >
                Turn ordinary sale photos into searchable inventory your team can price, tag, use at checkout, and track through the sale.
              </motion.p>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => goPrimary("hero")}
                  className={`${primaryCtaClass} w-full sm:w-auto`}
                >
                  {primaryLabel}
                  {isSignedIn ? <ArrowRight /> : null}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => scrollToSection("workflow", "hero")}
                  className="inline-flex min-h-[58px] w-full items-center justify-center gap-2 px-5 text-base font-bold text-white/85 transition hover:text-white sm:w-auto"
                >
                  How It Works
                  <ArrowRight />
                </motion.button>
              </div>

              {!isSignedIn && (
                <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-sm font-semibold text-white/72">
                  <span>25 free photo uploads</span>
                  <span aria-hidden="true">•</span>
                  <span>Multiple items per photo</span>
                  <span aria-hidden="true">•</span>
                  <span>No separate item limit</span>
                  <span aria-hidden="true">•</span>
                  <span>No credit card</span>
                </div>
              )}
            </div>

            <div className="mx-auto w-full max-w-[760px] rounded-[32px] border border-white/16 bg-white/12 p-3 shadow-[0_26px_72px_rgba(0,0,0,0.28)] backdrop-blur-xl">
              <div className="relative overflow-hidden rounded-[26px] border border-white/12 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-5 py-4 pr-[42%] sm:px-6 sm:py-5">
                  <div className="text-[13px] font-extrabold uppercase tracking-[0.12em] text-slate-500">
                    Product preview
                  </div>
                  <div className="mt-1 text-2xl font-extrabold text-blue-950">
                    One photo. 10 inventory items.
                  </div>
                </div>
                <div className="absolute right-4 top-4 z-10 w-[38%] max-w-[220px] overflow-hidden rounded-2xl border-4 border-white bg-white shadow-[0_18px_42px_rgba(15,23,42,0.28)]">
                  <img
                    src={PHOTO_SOURCE_IMAGE}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = PHOTO_SOURCE_FALLBACK;
                    }}
                    alt="Original sale source used to create the inventory items"
                    className="block h-auto w-full object-cover"
                    loading="lazy"
                    draggable="false"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-blue-950/90 px-2.5 py-1.5 text-center text-[12px] font-extrabold uppercase tracking-[0.1em] text-white sm:text-[13px]">
                    Source photo
                  </div>
                </div>
                <div className="bg-slate-50 p-4 lg:p-5">
                  <img
                    src={PHOTO_EFFICIENCY_IMAGE}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = PHOTO_EFFICIENCY_FALLBACK;
                    }}
                    alt="Draft inventory lines created from sale photos"
                    className="block h-auto w-full rounded-2xl border border-slate-200 bg-white object-contain object-top"
                    loading="lazy"
                    draggable="false"
                  />
                  <p className="mt-4 px-1 text-base font-semibold text-slate-600">
                    This source photo created the inventory records shown here.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust proof strip */}
      <section className="w-full border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 md:py-8">
          <div className="text-center text-[13px] font-extrabold uppercase tracking-[0.13em] text-slate-500">
            Built around the way estate-sale teams work
          </div>
          <div className="mt-6 grid gap-5 text-center sm:grid-cols-3">
            <div>
              <div className="text-3xl font-extrabold tracking-[-0.03em] text-blue-950">
                Create inventory faster
              </div>
              <div className="mt-1 text-base font-semibold text-slate-600">
                Turn one sale photo into multiple draft items with titles, descriptions, and suggested prices to review.
              </div>
            </div>
            <div>
              <div className="text-3xl font-extrabold tracking-[-0.03em] text-blue-950">
                Reduce typing at checkout
              </div>
              <div className="mt-1 text-base font-semibold text-slate-600">
                Use live search or QR codes to add items to carts instead of manually typing each item at checkout.
              </div>
            </div>
            <div>
              <div className="text-3xl font-extrabold tracking-[-0.03em] text-blue-950">
                More checkout lanes
              </div>
              <div className="mt-1 text-base font-semibold text-slate-600">
                Staff can build carts, allowing customers to pay by credit card without adding checkout hardware.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="workflow" className="w-full border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <SectionEyebrow>Three-step workflow</SectionEyebrow>
            <h2 className="mt-5 text-3xl font-extrabold text-blue-950 sm:text-5xl">
              Prepare the sale. Run checkout. Know what sold.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-700">
              Create inventory once, print price tags, build carts at checkout, and keep a record of what sold.
            </p>
          </div>

          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {workflowSteps.map((step, index) => (
              <div key={step.title} className="rounded-[28px] border border-blue-100 bg-white p-7 shadow-sm">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-950 text-base font-extrabold text-white">
                  {index + 1}
                </div>
                <div className="mt-6 text-2xl font-extrabold text-blue-950">{step.title}</div>
                <p className="mt-3 text-lg leading-relaxed text-gray-700">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-b border-blue-900/40 bg-blue-950 text-white">
        <div className="mx-auto grid max-w-[92rem] gap-10 px-4 py-14 sm:px-6 md:py-16 lg:grid-cols-[0.42fr_0.58fr] lg:items-start">
          <div>
            <SectionEyebrow dark>Faster checkout</SectionEyebrow>
            <h2 className="mt-5 text-3xl font-extrabold leading-tight tracking-[-0.025em] sm:text-5xl">
              Keep checkout moving throughout the home.
            </h2>
            <p className="mt-6 text-xl leading-relaxed text-white/84">
              Staff can build carts on logged-in phones or tablets, add prepared items with live search or QR scanning, and quickly enter anything that was not inventoried. Apply the day’s discount and sales tax, then record cash or outside-card sales—or create a payment QR for the customer.
            </p>
            <div className="mt-9 grid gap-4 text-lg font-extrabold text-white sm:grid-cols-2">
              {[
                "Build carts in multiple locations",
                "Add prepared items without retyping",
                "Add unlisted items when needed",
                "Track cash, card, discounts, and tax",
              ].map((item) => (
                <div key={item} className="flex min-h-[104px] items-center justify-center rounded-2xl border border-white/12 bg-white/9 px-5 py-5 text-center shadow-[0_14px_34px_rgba(0,0,0,0.14)]">
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="w-full max-w-[620px] lg:ml-auto">
            <div className="overflow-hidden rounded-[26px] bg-white text-blue-950 shadow-2xl">
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="text-[13px] font-extrabold uppercase tracking-[0.12em] text-slate-500">
                  Checkout workflow
                </div>
                <div className="mt-1 whitespace-nowrap text-2xl font-extrabold">Find the item. Build the cart. Complete the sale.</div>
              </div>

              <div className="bg-slate-50 px-6 py-5 sm:px-8 sm:py-6">
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 shadow-sm">
                  <img
                    src={CHECKOUT_MODAL_IMAGE}
                    alt="Checkout modal with cart items, live item search, sales tax, and QR payment option"
                    className="mx-auto block max-h-[700px] w-auto max-w-full object-contain object-top"
                    loading="lazy"
                    draggable="false"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full border-b border-gray-100 bg-slate-50">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 md:py-20 lg:grid-cols-[1fr_0.92fr] lg:items-center">
          <div className="overflow-hidden rounded-[32px] border border-blue-100 bg-white shadow-xl">
            <img
              src={START_SMALL_IMAGE}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = START_SMALL_FALLBACK;
              }}
              alt="Scucci screen showing uploaded sale photos and draft inventory"
              className="block h-auto w-full object-contain"
              loading="lazy"
              draggable="false"
            />
          </div>

          <div>
            <SectionEyebrow>Start small</SectionEyebrow>
            <h2 className="mt-5 text-3xl font-extrabold leading-tight tracking-[-0.025em] text-blue-950 sm:text-5xl">
              Test Scucci with photos from a past sale.
            </h2>
            <p className="mt-5 max-w-3xl text-lg leading-relaxed text-gray-700">
              Upload a few existing sale photos and review the inventory Scucci creates. If the output fits your workflow, use it for one room or one batch of photos at your next sale.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => goPrimary("low_risk")}
                className={`${darkPrimaryCtaClass} w-full sm:w-auto`}
              >
                {primaryLabel}
                {isSignedIn ? <ArrowRight /> : null}
              </motion.button>
            </div>
            {!isSignedIn && (
              <div className="mt-4 text-base font-semibold text-slate-600">
                25 photo uploads for 30 days. No credit card required.
              </div>
            )}
          </div>
        </div>
      </section>

      <section id="faq" className="w-full border-b border-gray-100 bg-white">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 md:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <SectionEyebrow>Quick answers</SectionEyebrow>
            <h2 className="mt-5 text-3xl font-extrabold text-blue-950 sm:text-5xl">
              What estate-sale operators ask before trying Scucci
            </h2>
          </div>

          <div className="mt-10 space-y-4">
            <QA
              q="Do I have to inventory every item?"
              a="No. Start with one room, one category, or only the items you want tagged and tracked. At checkout, staff can quickly add anything that was not inventoried in advance, including its title, category, quantity, and final price."
            />
            <QA
              q="Do I have to change how I accept payments?"
              a="No. Keep accepting cash or using your current card processor. Scucci records those sales alongside the items, discounts, tax, and final total. Connected Stripe accounts can also create payment QR codes so customers can pay by credit card on their own phones."
            />
            <QA
              q="Do I need special printing equipment?"
              a="No. Use a standard home or office printer with supported Avery or compatible sheet labels. Print tags before the sale or bring your printer and print on-site. Optional thermal-label formats are available for teams that already use them."
            />
            <QA
              q="Can I test Scucci without using it on a live sale?"
              a="Yes. Upload photos from a previous sale and review the draft inventory Scucci creates. Each photo can produce multiple editable items with titles, descriptions, and suggested prices, so you can judge the output before using Scucci at your next sale."
            />
            <QA
              q="What is included when I start free?"
              a="You receive 25 photo uploads for 30 days with no credit card required. Uploads are counted by photo, not by the number of items identified, and there is no separate inventory-item limit."
            />
          </div>
        </div>
      </section>

      <section className="w-full bg-white">
        <div className="mx-auto max-w-5xl px-4 py-16 text-center sm:px-6 md:py-20">
          <h2 className="text-3xl font-extrabold tracking-[-0.025em] text-blue-950 sm:text-5xl">
            See what Scucci builds from your sale photos.
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-gray-700">
            Upload a few sale photos and review the inventory Scucci creates before you decide whether to use it for your next sale.
          </p>

          <div className="mt-8 flex flex-col items-center justify-center gap-3">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => goPrimary("final_cta")}
              className={`${darkPrimaryCtaClass} w-full sm:w-auto`}
            >
              {primaryLabel}
              {isSignedIn ? <ArrowRight /> : null}
            </motion.button>
            {!isSignedIn && (
              <div className="text-base text-slate-600">
                Includes 25 free photo uploads for 30 days. No credit card required.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}